import {AfterViewChecked, AfterViewInit, Component, Input, OnChanges, SimpleChanges} from '@angular/core';

interface RGB {
    r: number;
    g: number;
    b: number;
}

@Component({
    selector: 'wm-contrast-tooltip-view',
    templateUrl: './contrast-tooltip-view.component.html',
    styleUrls: ['./contrast-tooltip-view.component.less']
})
export class ContrastTooltipViewComponent implements OnChanges, AfterViewChecked, AfterViewInit {
    @Input() isVisible: boolean;
    @Input() analysisItem: any;
    @Input() canvas: HTMLCanvasElement;
    @Input() imageDataUrl: string;

    tooltipX: number;
    tooltipY: number;

    highlightX: number;
    highlightY: number;
    highlightW: number;
    highlightH: number;

    contrastRatio: number;
    text: string;

    savedPixels: ImageData;
    shouldSavePixels: boolean;
    ctx: CanvasRenderingContext2D;

    tooltipAtBottom: boolean = true;

    shouldDisplay() {
        return this.isVisible && !!this.analysisItem;
    }

    ngAfterViewChecked() {
        if (this.shouldSavePixels) {
            // canvas hasn't completely rendered yet so setTimeout
            this.shouldSavePixels = false;
            setTimeout(() => {
                const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height, {colorSpace: "srgb"});
                this.savedPixels = this.cloneImageData(imageData);
            }, 100);
        }
    }

    ngAfterViewInit() {
        this.ctx = this.canvas.getContext('2d', {willReadFrequently: true, colorSpace: "srgb"})!!;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ("imageDataUrl" in changes) {
            this.shouldSavePixels = true;
        }

        if (!this.isVisible || !this.analysisItem)
            return;

        this.contrastRatio = this.analysisItem['contrastRatio'].toFixed(3);
        this.text = this.analysisItem['text'];

        const rectCanvas: DOMRect = this.canvas.getBoundingClientRect();
        const dx = (this.analysisItem['textWindow']['x']) / this.canvas.width;
        const dy = (this.analysisItem['textWindow']['y']) / this.canvas.height;
        const dw = (this.analysisItem['textWindow']['w']) / this.canvas.width;
        const dh = (this.analysisItem['textWindow']['h']) / this.canvas.height;

        this.highlightX = dx * rectCanvas.width + rectCanvas.left;
        this.highlightY = dy * rectCanvas.height + rectCanvas.top;
        this.highlightW = dw * rectCanvas.width;
        this.highlightH = dh * rectCanvas.height;

        const tooltipWidth = 250;
        const tooltipHeight = 250;

        this.tooltipAtBottom = true;
        this.tooltipX = this.highlightX - (tooltipWidth / 2) + this.highlightW / 2;
        this.tooltipY = this.highlightY + this.highlightH;

        if (this.tooltipY > ((rectCanvas.height + rectCanvas.top) / 2)) {
            this.tooltipAtBottom = false;
            this.tooltipY = this.highlightY - tooltipHeight;
        }
    }

    paintFgMask() {
        this.paintMaskInWindow(this.analysisItem['fgMask']);
    }

    paintBgMask() {
        this.paintMaskInWindow(this.analysisItem['bgMask']);
    }

    paintFg() {
        this.paintMatchingColor(this.analysisItem['fgColor'], this.analysisItem['fgMask']);
    }

    paintBg() {
        this.paintMatchingColor(this.analysisItem['bgColor'], this.analysisItem['bgMask']);
    }

    cloneImageData(imageData: ImageData) {
        const clone = new ImageData(
            new Uint8ClampedArray(imageData.data),
            imageData.width,
            imageData.height
        );
        return clone;
    }

    resetPaints() {
        const wx = this.analysisItem['textWindow']['x'];
        const wy = this.analysisItem['textWindow']['y'];
        const ww = this.analysisItem['textWindow']['w'];
        const wh = this.analysisItem['textWindow']['h'];
        this.ctx.putImageData(this.savedPixels, 0, 0, wx, wy, ww, wh);
    }

    paintMaskInWindow(mask: [any]) {
        const wx = this.analysisItem['textWindow']['x'];
        const wy = this.analysisItem['textWindow']['y'];
        const ww = this.analysisItem['textWindow']['w'];
        const wh = this.analysisItem['textWindow']['h'];

        const imageData = new ImageData(ww, wh);
        const newColor = {'r': 255, 'g': 0, 'b': 0, 'a': 255};

        for (let r = 0; r < wh; r++) {
            for (let c = 0; c < ww; c++) {
                const ti = (r * ww + c) * 4;
                if (this.inMask(r, c, mask, ww, wh)) {
                    // each pixel's r comes every 4th position i.e [r, g, b, a, r, g, b, a, ...]
                    imageData.data[ti] = newColor.r;
                    imageData.data[ti + 1] = newColor.g;
                    imageData.data[ti + 2] = newColor.b;
                    imageData.data[ti + 3] = newColor.a;
                } else {
                    imageData.data[ti] = 0;
                    imageData.data[ti + 1] = 0;
                    imageData.data[ti + 2] = 0;
                    imageData.data[ti + 3] = 255;
                }
            }
        }
        this.ctx.putImageData(imageData, wx, wy);
    }

    colorDistance(rgb1: RGB, rgb2: RGB) {
        // source: https://www.compuphase.com/cmetric.htm
        const rmean = (rgb1.r + rgb2.r) / 2;
        const r = rgb1.r - rgb2.r;
        const g = rgb1.g - rgb2.g;
        const b = rgb1.b - rgb2.b;
        return Math.sqrt((((512 + rmean) * r * r) >> 8) + 4 * g * g + (((767 - rmean) * b * b) >> 8));
    }

    paintMatchingColor(hexColor: string, mask: [any]) {
        const wx = this.analysisItem['textWindow']['x'];
        const wy = this.analysisItem['textWindow']['y'];
        const ww = this.analysisItem['textWindow']['w'];
        const wh = this.analysisItem['textWindow']['h'];

        // paint color should be sufficiently different for it to be visible
        const targetRgb = this.hexToRgb(hexColor);
        let newColor = {'r': 255, 'g': 0, 'b': 0, 's': 255};
        const distance = this.colorDistance(targetRgb, newColor);
        if (distance < 100) {
            newColor = {'r': 0, 'g': 0, 'b': 0, 's': 255};
        }

        this.resetPaints();

        const imageData = this.ctx.getImageData(wx, wy, ww, wh, {colorSpace: "srgb"});
        for (let r = 0; r < wh; r++) {
            for (let c = 0; c < ww; c++) {
                const i = (r * ww + c) * 4;
                if (this.inMask(r, c, mask, ww, wh) &&
                    imageData.data[i] == targetRgb.r &&
                    imageData.data[i + 1] == targetRgb.g &&
                    imageData.data[i + 2] == targetRgb.b)
                {
                    imageData.data[i] = newColor.r;
                    imageData.data[i + 1] = newColor.g;
                    imageData.data[i + 2] = newColor.b;
                    imageData.data[i + 3] = newColor.s;
                }
            }
        }
        this.ctx.putImageData(imageData, wx, wy);
    }

    hexToRgb(hex: string) {
        const bigint = parseInt(hex.slice(1), 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return {r, g, b};
    }

    inMask(r: number, c: number, bitPackedMask: [number], width: number, height: number) {
        /*
        mask is an uint8 bitpacked array of boolean matrix
        Eg:
              [[1, 1, 0],
               [0, 1, 1],
               [1, 0, 1]]
            gets packed into [206, 128]
        As:
            11001110 = 206
            10000000 = 128
         */
        const packBitSize = 8;
        const i = (r * width) + c;
        const numIndex = Math.floor(i / packBitSize);
        const bitIndex = i % packBitSize;
        return (bitPackedMask[numIndex] & (1 << (packBitSize - 1 - bitIndex))) != 0;
    }
}
